/**
 * Sentry API Client Configuration
 *
 * Provides request configuration for @sentry/api SDK functions,
 * including authentication, retry logic, timeout, and multi-region support.
 *
 * Instead of managing client instances, we pass configuration per-request
 * through the SDK function options (baseUrl, fetch, headers).
 */

import { getTraceData } from "@sentry/node-core/light";
import { maybeWarnEnvTokenIgnored } from "./auth-hint.js";
import { computeInvalidationPrefixes } from "./cache-keys.js";
import {
  DEFAULT_SENTRY_URL,
  getConfiguredSentryUrl,
  getUserAgent,
} from "./constants.js";
import { applyCustomHeaders } from "./custom-headers.js";
import { getAuthToken, refreshToken } from "./db/auth.js";
import { logger } from "./logger.js";
import {
  getCachedResponse,
  invalidateCachedResponsesMatching,
  storeCachedResponse,
} from "./response-cache.js";
import { withTracingSpan } from "./telemetry.js";

const log = logger.withTag("http");

/** Request timeout in milliseconds */
const REQUEST_TIMEOUT_MS = 30_000;

/** Maximum retry attempts for failed requests */
const MAX_RETRIES = 2;

/** Maximum backoff delay between retries in milliseconds */
const MAX_BACKOFF_MS = 10_000;

/** HTTP status codes that trigger automatic retry */
const RETRYABLE_STATUS_CODES = [408, 429, 500, 502, 503, 504];

/** Header to mark requests as retries, preventing infinite token refresh loops */
const RETRY_MARKER_HEADER = "x-sentry-cli-retry";

/** Calculate exponential backoff delay, capped at MAX_BACKOFF_MS */
function backoffDelay(attempt: number): number {
  return Math.min(1000 * 2 ** attempt, MAX_BACKOFF_MS);
}

/** Check if an error is a user-initiated abort */
function isUserAbort(error: unknown, signal?: AbortSignal | null): boolean {
  return (
    error instanceof DOMException &&
    error.name === "AbortError" &&
    Boolean(signal?.aborted)
  );
}

/**
 * Prepare request headers with auth token and default headers.
 *
 * Only sets Authorization and User-Agent. Content-Type is intentionally NOT
 * set here — callers are responsible for setting it based on their needs:
 * - SDK functions set their own Content-Type
 * - apiRequestToRegion always sends JSON and sets it explicitly
 * - rawApiRequest may or may not want Content-Type (e.g., string bodies)
 *
 * When `init` is undefined (the SDK passes only a Request object), headers are
 * read from the Request object to preserve Content-Type and other headers set
 * by the SDK. Without this, fetch(Request, {headers}) would override the
 * Request's headers with our empty headers, stripping Content-Type and causing
 * HTTP 415 errors on Node.js (which strictly follows the spec).
 *
 * The returned Headers instance is intentionally shared and mutated across
 * retry attempts (e.g., handleUnauthorized updates the Authorization header
 * and sets the retry marker). Do not clone before passing to retry logic.
 */
function prepareHeaders(
  input: Request | string | URL,
  init: RequestInit | undefined,
  token: string
): Headers {
  // When the SDK calls fetch(request) with no init, read headers from the Request
  // object to preserve Content-Type. On Node.js, fetch(request, {headers}) replaces
  // the Request's headers entirely per spec, so we must carry them forward explicitly.
  const sourceHeaders =
    init?.headers ?? (input instanceof Request ? input.headers : undefined);
  const headers = new Headers(sourceHeaders);
  headers.set("Authorization", `Bearer ${token}`);
  if (!headers.has("User-Agent")) {
    headers.set("User-Agent", getUserAgent());
  }

  // Inject distributed tracing headers to connect CLI spans to backend traces.
  // Manual injection is required because Bun's fetch doesn't fire undici
  // diagnostics channels, so the SDK's nativeNodeFetchIntegration cannot work.
  // When telemetry is disabled, getTraceData() returns {} — no headers injected.
  const traceData = getTraceData();
  if (traceData["sentry-trace"]) {
    headers.set("sentry-trace", traceData["sentry-trace"]);
  }
  if (traceData.baggage) {
    headers.set("baggage", traceData.baggage);
  }

  // Inject user-configured custom headers for self-hosted proxies (IAP, mTLS, etc.)
  applyCustomHeaders(headers);

  return headers;
}

/**
 * Handle 401 response by refreshing the token.
 * @returns true if the token was refreshed and request should be retried
 */
async function handleUnauthorized(headers: Headers): Promise<boolean> {
  if (headers.get(RETRY_MARKER_HEADER)) {
    return false;
  }
  // refreshToken handles the token selection: it refreshes OAuth when OAuth is
  // the effective auth source, or returns the env token without refresh when
  // SENTRY_FORCE_ENV_TOKEN is set. If the token can't be refreshed (env token,
  // no refresh token), `refreshed` is false and the 401 propagates.
  try {
    const { token: newToken, refreshed } = await refreshToken({ force: true });
    if (refreshed) {
      headers.set("Authorization", `Bearer ${newToken}`);
      headers.set(RETRY_MARKER_HEADER, "1");
      return true;
    }
  } catch {
    // Token refresh failed
  }
  return false;
}

/** Link an external abort signal to an AbortController */
function linkAbortSignal(
  signal: AbortSignal | undefined | null,
  controller: AbortController
): void {
  if (!signal) {
    return;
  }
  if (signal.aborted) {
    controller.abort();
    return;
  }
  signal.addEventListener("abort", () => controller.abort(), { once: true });
}

/**
 * Execute a single fetch attempt with timeout.
 *
 * @returns The Response, or throws on network/timeout errors
 */
async function fetchWithTimeout(
  input: Request | string | URL,
  init: RequestInit | undefined,
  headers: Headers,
  externalSignal?: AbortSignal | null
): Promise<Response> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  linkAbortSignal(externalSignal, controller);

  try {
    const response = await fetch(input, {
      ...init,
      headers,
      signal: controller.signal,
    });
    return response;
  } finally {
    clearTimeout(timeout);
  }
}

/** Result of a single fetch attempt - drives the retry loop */
type AttemptResult =
  | { action: "done"; response: Response }
  | { action: "retry" }
  | { action: "throw"; error: unknown };

/**
 * Decide what to do with a successful HTTP response.
 * Returns 'done' for final responses, 'retry' for retryable errors and 401s.
 */
async function handleResponse(
  response: Response,
  headers: Headers,
  isLastAttempt: boolean
): Promise<AttemptResult> {
  if (response.status === 401) {
    const refreshed = await handleUnauthorized(headers);
    return refreshed ? { action: "retry" } : { action: "done", response };
  }

  if (RETRYABLE_STATUS_CODES.includes(response.status) && !isLastAttempt) {
    return { action: "retry" };
  }

  return { action: "done", response };
}

/**
 * Decide what to do with a fetch error.
 * Throws immediately for user-initiated aborts or last attempt;
 * returns 'retry' otherwise.
 */
function handleFetchError(
  error: unknown,
  signal: AbortSignal | undefined | null,
  isLastAttempt: boolean
): AttemptResult {
  if (isUserAbort(error, signal)) {
    return { action: "throw", error };
  }
  if (isLastAttempt) {
    return { action: "throw", error };
  }
  return { action: "retry" };
}

/** Extract the full URL string from a fetch input */
function extractFullUrl(input: Request | string | URL): string {
  if (typeof input === "string") {
    return input;
  }
  if (input instanceof URL) {
    return input.href;
  }
  return input.url;
}

/** Extract the URL pathname for span naming */
function extractUrlPath(input: Request | string | URL): string {
  const raw = extractFullUrl(input);
  try {
    return new URL(raw).pathname;
  } catch {
    return raw;
  }
}

/**
 * Attempt to serve a GET request from the response cache.
 * Returns the cached Response if valid, or undefined on miss.
 *
 * @param requestHeaders - Headers that were (or will be) sent with the request,
 *   needed for correct `Vary` handling in CachePolicy freshness checks.
 */
async function tryCacheHit(
  method: string,
  fullUrl: string,
  requestHeaders: Record<string, string>
): Promise<Response | undefined> {
  if (method !== "GET") {
    return;
  }
  return await getCachedResponse(method, fullUrl, requestHeaders);
}

/**
 * Store a successful GET response in the cache (fire-and-forget).
 * Clones the response so the original body stream is preserved for the caller.
 *
 * @param requestHeaders - Headers sent with the request, stored in CachePolicy
 *   for future `Vary`-aware freshness checks.
 */
function cacheResponse(
  method: string,
  fullUrl: string,
  requestHeaders: Record<string, string>,
  response: Response
): void {
  if (method !== "GET" || !response.ok) {
    return;
  }
  // Cast needed: Bun extends Response with extra properties (toJSON, count, getAll)
  // that .clone() doesn't carry over, but our cache only reads standard Response API
  storeCachedResponse(
    method,
    fullUrl,
    requestHeaders,
    response.clone() as Response
  ).catch(() => {
    // Non-fatal: cache write failures don't affect the response
  });
}

/**
 * Auto-invalidate cache entries that a successful non-GET mutation
 * made stale. Awaited so a subsequent read in the same command sees
 * fresh data. Prefix computation: {@link computeInvalidationPrefixes}.
 *
 * Never throws: a post-mutation housekeeping failure must not convert
 * a successful mutation into a caller-visible error. Defense-in-depth
 * for future regressions — the helpers we call are already no-throw
 * today.
 */
async function invalidateAfterMutation(
  method: string,
  fullUrl: string,
  response: Response
): Promise<void> {
  if (method === "GET" || !response.ok) {
    return;
  }
  try {
    const prefixes = computeInvalidationPrefixes(fullUrl, getApiBaseUrl());
    await Promise.all(
      prefixes.map((prefix) => invalidateCachedResponsesMatching(prefix))
    );
  } catch {
    /* best-effort: mutation already succeeded upstream */
  }
}

/** Build a `{ authorization }` header map from a bearer token, or `{}` if absent. */
function authHeaders(token: string | undefined): Record<string, string> {
  return token ? { authorization: `Bearer ${token}` } : {};
}

/**
 * Authenticate and execute a request with retry logic.
 *
 * Refreshes the auth token, then retries the request up to `MAX_RETRIES` times
 * with exponential backoff on transient errors.
 */
async function fetchWithRetry(
  input: Request | string | URL,
  init: RequestInit | undefined,
  method: string,
  fullUrl: string
): Promise<Response> {
  const { token } = await refreshToken();
  const headers = prepareHeaders(input, init, token);

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    const isLastAttempt = attempt === MAX_RETRIES;
    const result = await executeAttempt(input, init, headers, isLastAttempt);

    if (result.action === "done") {
      // Use getAuthToken() instead of captured `token` — after a 401 refresh,
      // handleUnauthorized stores a new token in the DB
      cacheResponse(
        method,
        fullUrl,
        authHeaders(getAuthToken()),
        result.response
      );
      await invalidateAfterMutation(method, fullUrl, result.response);
      return result.response;
    }
    if (result.action === "throw") {
      throw result.error;
    }

    const delay = backoffDelay(attempt);
    log.debug(
      `${method} ${new URL(fullUrl).pathname} → retry ${attempt + 1}/${MAX_RETRIES} after ${delay}ms`
    );
    await Bun.sleep(delay);
  }

  // Unreachable: the last attempt always returns 'done' or 'throw'
  throw new Error("Exhausted all retry attempts");
}

/**
 * Create a fetch function with authentication, timeout, retry, caching, and 401 refresh.
 *
 * This wraps the native fetch with:
 * - **Response caching** for GET requests (checked before hitting the network)
 * - Auth token injection (Bearer token)
 * - Request timeout via AbortController
 * - Automatic retry on transient HTTP errors (408, 429, 5xx)
 * - 401 handling: force-refreshes the token and retries once
 * - Exponential backoff between retries
 * - User-Agent header for API analytics
 * - Automatic HTTP span tracing for every request
 *
 * Cache is checked first — on a hit, auth refresh, timeout, and retry logic are
 * all skipped. On a miss or for non-GET methods, the full authenticated flow runs
 * and successful GET responses are stored in the cache afterward.
 *
 * @returns A fetch-compatible function for use with @sentry/api SDK functions
 */
function createAuthenticatedFetch(): (
  input: Request | string | URL,
  init?: RequestInit
) => Promise<Response> {
  return function authenticatedFetch(
    input: Request | string | URL,
    init?: RequestInit
  ): Promise<Response> {
    // Once-per-process hint when env-var auth token is shadowed by a
    // stored OAuth login. Runs here (rather than at command entry) so
    // the hint only fires for commands that actually exercise auth —
    // `sentry help` and similar local-only commands stay quiet.
    // Internally rate-limited and cheap on repeat calls.
    maybeWarnEnvTokenIgnored();

    const method =
      init?.method ?? (input instanceof Request ? input.method : "GET");
    const urlPath = extractUrlPath(input);

    return withTracingSpan(
      `${method} ${urlPath}`,
      "http.client",
      async (span) => {
        const fullUrl = extractFullUrl(input);
        const startTime = performance.now();

        // Check cache before auth/retry for GET requests.
        // Uses current token (no refresh) so lookups are fast but Vary-correct.
        const cached = await tryCacheHit(
          method,
          fullUrl,
          authHeaders(getAuthToken())
        );
        if (cached) {
          span.setAttribute("http.response.status_code", cached.status);
          log.debug(
            `${method} ${urlPath} → ${cached.status} (cache hit, ${(performance.now() - startTime).toFixed(0)}ms)`
          );
          return cached;
        }

        const response = await fetchWithRetry(input, init, method, fullUrl);
        span.setAttribute("http.response.status_code", response.status);
        if (!response.ok) {
          span.setStatus({ code: 2, message: `${response.status}` });
        }
        log.debug(
          `${method} ${urlPath} → ${response.status} (${(performance.now() - startTime).toFixed(0)}ms)`
        );
        return response;
      },
      { "http.request.method": method, "url.path": urlPath }
    );
  };
}

/**
 * Execute a single fetch attempt and classify the outcome.
 */
async function executeAttempt(
  input: Request | string | URL,
  init: RequestInit | undefined,
  headers: Headers,
  isLastAttempt: boolean
): Promise<AttemptResult> {
  try {
    const response = await fetchWithTimeout(input, init, headers, init?.signal);
    return handleResponse(response, headers, isLastAttempt);
  } catch (error) {
    return handleFetchError(error, init?.signal, isLastAttempt);
  }
}

/** Singleton authenticated fetch instance - reused across all requests */
let cachedFetch: typeof fetch | null = null;

/**
 * Get the shared authenticated fetch instance.
 * Cast to `typeof fetch` for compatibility with @sentry/api SDK options.
 */
function getAuthenticatedFetch(): typeof fetch {
  if (!cachedFetch) {
    cachedFetch = createAuthenticatedFetch() as unknown as typeof fetch;
  }
  return cachedFetch;
}

/**
 * Get the Sentry API base URL.
 * Supports self-hosted instances via SENTRY_URL env var.
 */
export function getApiBaseUrl(): string {
  return getConfiguredSentryUrl() ?? DEFAULT_SENTRY_URL;
}

/**
 * Get the control silo URL.
 * This is always sentry.io for SaaS, or the custom URL for self-hosted.
 *
 * Read lazily (not at module load) so that SENTRY_URL set after import
 * (e.g., from URL argument parsing for self-hosted instances) is respected.
 */
export function getControlSiloUrl(): string {
  return getConfiguredSentryUrl() ?? DEFAULT_SENTRY_URL;
}

/**
 * Get request configuration for an @sentry/api SDK function call.
 *
 * Returns the common options needed by every SDK function call:
 * - `baseUrl`: The API base URL for the target region
 * - `fetch`: Authenticated fetch with retry, timeout, and 401 refresh
 * - `throwOnError`: Always false (we handle errors ourselves)
 *
 * @param regionUrl - The base URL for the target region (e.g., https://us.sentry.io)
 * @returns Configuration object to spread into SDK function options
 *
 * @example
 * ```ts
 * const config = getSdkConfig("https://us.sentry.io");
 * const result = await listYourOrganizations({ ...config });
 * ```
 */
export function getSdkConfig(regionUrl: string) {
  const normalizedBase = regionUrl.endsWith("/")
    ? regionUrl.slice(0, -1)
    : regionUrl;

  return {
    // SDK functions already include /api/0/ in their URL paths,
    // so baseUrl should be the plain region URL without /api/0.
    baseUrl: normalizedBase,
    fetch: getAuthenticatedFetch(),
    throwOnError: false as const,
  };
}

/**
 * Get SDK config for the default API (control silo or self-hosted).
 */
export function getDefaultSdkConfig() {
  return getSdkConfig(getApiBaseUrl());
}

/**
 * Get SDK config for the control silo.
 * Used for endpoints that are always on the control silo (OAuth, user accounts, regions).
 */
export function getControlSdkConfig() {
  return getSdkConfig(getControlSiloUrl());
}

/**
 * Reset the cached fetch instance.
 * Useful for testing or when auth state changes.
 */
export function resetAuthenticatedFetch(): void {
  cachedFetch = null;
}
